import React, { useState, useEffect } from 'react';
import { FileText, Calendar, AlignLeft, FileImage, Image, Plus, Check, X, Loader2, Eye } from 'lucide-react';

function AnnouncementForm({ announcement, onSave, onCancel }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [shortDetail, setShortDetail] = useState('');
  const [modalDetail, setModalDetail] = useState('');
  const [cardImage, setCardImage] = useState('');
  const [modalImage, setModalImage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState('card'); // 'card' or 'modal'

  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title || '');
      if (announcement.date) {
        const dateObj = new Date(announcement.date);
        setDate(dateObj.toISOString().split('T')[0]);
      }
      setShortDetail(announcement.shortDetail || '');
      setModalDetail(announcement.modalDetail || '');
      setCardImage(announcement.cardImage || '');
      setModalImage(announcement.modalImage || '');
    } else {
      setTitle(''); setDate(''); setShortDetail(''); setModalDetail(''); setCardImage(''); setModalImage('');
    }
  }, [announcement]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!title.trim() || !shortDetail.trim() || !modalDetail.trim() || !cardImage.trim() || !modalImage.trim()) {
      alert('Lütfen tüm alanları doldurun.');
      setIsSubmitting(false);
      return;
    }
    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('date', date || new Date().toISOString());
    formData.append('shortDetail', shortDetail.trim());
    formData.append('modalDetail', modalDetail.trim());
    formData.append('cardImage', cardImage.trim());
    formData.append('modalImage', modalImage.trim());
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Duyuru eklenirken hata oluştu:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPreviewDate = () => {
    if (!date) return new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
    return new Date(date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
        <div>
          <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
            <FileText className="w-3 h-3" /> Başlık <span className="text-red-400">*</span>
          </label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Duyuru başlığı..." required
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
            <Calendar className="w-3 h-3" /> Tarih
          </label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
            <AlignLeft className="w-3 h-3" /> Kısa Açıklama <span className="text-red-400">*</span>
          </label>
          <textarea value={shortDetail} onChange={(e) => setShortDetail(e.target.value)} placeholder="Kart açıklaması..." required rows="2"
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none" />
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
            <AlignLeft className="w-3 h-3" /> Detaylı Açıklama <span className="text-red-400">*</span>
          </label>
          <textarea value={modalDetail} onChange={(e) => setModalDetail(e.target.value)} placeholder="Modal açıklaması..." required rows="4"
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none" />
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
            <FileImage className="w-3 h-3" /> Kart Resmi URL <span className="text-red-400">*</span>
          </label>
          <input type="url" value={cardImage} onChange={(e) => setCardImage(e.target.value)} placeholder="https://..." required
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs text-zinc-500 mb-1.5">
            <Image className="w-3 h-3" /> Modal Resmi URL <span className="text-red-400">*</span>
          </label>
          <input type="url" value={modalImage} onChange={(e) => setModalImage(e.target.value)} placeholder="https://..." required
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500 transition-colors" />
        </div>

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (announcement ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
            {announcement ? 'Güncelle' : 'Ekle'}
          </button>
          <button type="button" onClick={onCancel} disabled={isSubmitting}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-lg transition-colors flex items-center gap-2">
            <X className="w-4 h-4" /> İptal
          </button>
        </div>
      </form>

      {/* Live Preview */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-zinc-400 text-xs">
            <Eye className="w-4 h-4" /> Canlı Önizleme
          </div>
          <div className="flex gap-1">
            <button type="button" onClick={() => setPreviewMode('card')}
              className={`px-3 py-1 text-xs rounded-lg transition-colors ${previewMode === 'card' ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
              Kart
            </button>
            <button type="button" onClick={() => setPreviewMode('modal')}
              className={`px-3 py-1 text-xs rounded-lg transition-colors ${previewMode === 'modal' ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
              Modal
            </button>
          </div>
        </div>

        {previewMode === 'card' ? (
          <div className="bg-zinc-800 rounded-lg overflow-hidden">
            <div className="h-32 bg-zinc-700 relative">
              {cardImage ? (
                <img src={cardImage} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">Resim URL'si girin</div>
              )}
            </div>
            <div className="p-4">
              <h3 className="text-white font-medium text-sm mb-1 line-clamp-1">{title || 'Duyuru Başlığı'}</h3>
              <p className="text-zinc-500 text-xs mb-2">{formatPreviewDate()}</p>
              <p className="text-zinc-400 text-xs line-clamp-2">{shortDetail || 'Kısa açıklama burada görünecek...'}</p>
              <button className="mt-3 text-violet-400 text-xs hover:text-violet-300 transition-colors">Detayları Gör →</button>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-800 rounded-lg overflow-hidden">
            <div className="h-40 bg-zinc-700 relative">
              {modalImage ? (
                <img src={modalImage} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">Modal resmi URL'si girin</div>
              )}
            </div>
            <div className="p-4">
              <h3 className="text-white font-medium mb-2">{title || 'Duyuru Başlığı'}</h3>
              <p className="text-zinc-500 text-xs mb-3">{formatPreviewDate()}</p>
              <p className="text-zinc-400 text-sm whitespace-pre-wrap">{modalDetail || 'Detaylı açıklama burada görünecek...'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnnouncementForm;
